import React from 'react';
import { X, GripVertical } from 'lucide-react';
import { SortRule } from '../../hooks/useMultiSort';
import { CustomSelect } from '../common/CustomSelect';

export interface SortField {
  id: string;
  name: string;
  type?: string;
}

export interface SortRuleRowProps {
  sort: SortRule;
  index: number;
  fields: SortField[];
  isDragging: boolean;
  showDropIndicator: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onDragEnd: () => void;
  onUpdate: (updates: Partial<SortRule>) => void;
  onRemove: () => void;
}

export const SortRuleRow: React.FC<SortRuleRowProps> = ({
  sort,
  index,
  fields,
  isDragging,
  showDropIndicator,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onUpdate,
  onRemove,
}) => {
  return (
    <div className="relative">
      {showDropIndicator && (
        <div className="absolute -top-1.5 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10" />
      )}
      <div
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        className={`flex items-center gap-2 transition-opacity ${isDragging ? 'opacity-50' : ''}`}
      >
        {/* Drag handle */}
        <button
          type="button"
          aria-label={`Reorder sort rule ${index + 1}`}
          className="flex items-center justify-center w-6 h-8 rounded text-gray-300 hover:text-gray-500 hover:bg-gray-50 cursor-grab transition-all flex-shrink-0"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>

        {/* Column select */}
        <div className="flex-1 min-w-[140px]">
          <CustomSelect
            value={sort.fieldId}
            onChange={(value) => onUpdate({ fieldId: value })}
            options={[
              { value: '', label: 'Select field...' },
              ...fields.map(field => ({ value: field.id, label: field.name }))
            ]}
            placeholder="Select field..."
            aria-label={`Sort field ${index + 1}`}
          />
        </div>

        {/* Direction select */}
        <div className="w-24 flex-shrink-0">
          <CustomSelect
            value={sort.direction}
            onChange={(value) => onUpdate({ direction: value as 'asc' | 'desc' })}
            options={[
              { value: 'asc', label: 'A → Z' },
              { value: 'desc', label: 'Z → A' }
            ]}
            aria-label={`Sort direction ${index + 1}`}
          />
        </div>

        {/* Remove button */}
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove sort rule ${index + 1}`}
          className="flex items-center justify-center w-6 h-8 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
